/**
 * Thin loader + initialiser for Valor's Passage v2 SDK - lightbox variant.
 *
 * The SDK renders a full-page modal overlay (lightbox) when the caller
 * invokes ``instance.openLightbox()``.  Card data is entered inside the
 * Valor-controlled modal; ``onTokenReceived`` fires on submission with an
 * opaque token.  Raw PAN never reaches our application code.
 *
 * Lightbox mode removes the need to hide an SDK-rendered submit button and
 * avoids inline event handler CSP violations that the inline variant triggers.
 *
 * Usage:
 *   const { instance } = await initializePassageV2Form({
 *     hostElementId: "valor-passage-host",
 *     clientToken,
 *     epi,
 *     defaults: { cardholderName, phone },
 *     onTokenReceived: (token) => handleCardToken(token),
 *     onError:         (err)   => setVendorError(err),
 *     onSessionExpired: ()     => refetchToken(),
 *   });
 *
 *   // Open the lightbox when the cashier clicks Pay (after L3 validation):
 *   instance.openLightbox();
 *
 *   // On unmount:
 *   instance.destroy?.();
 */

export interface PassageInstance {
  openLightbox: () => void;
  closeLightbox?: () => void;
  destroy?: () => void;
}

export interface PassageFormOptions {
  hostElementId: string;
  clientToken: string;
  epi: string;
  /** When set, the Passage SDK POSTs the card token to this URL instead of
   *  handling it client-side. Use with onSuccess instead of onTokenReceived. */
  formAction?: string;
  defaults?: {
    cardholderName?: string;
    phone?: string;
    zip?: string;
  };
  isDemo?: boolean;
  onTokenReceived?: (cardToken: string) => void;
  /** Fires after a successful server-side charge (formAction flow only). */
  onSuccess?: () => void;
  onError?: (err: string) => void;
  onSessionExpired?: () => void;
}

const PASSAGE_V2_SRC = "https://js.valorpaytech.com/V2/js/Passage.min.js";

let _scriptPromise: Promise<void> | null = null;

function _loadScript(): Promise<void> {
  if (_scriptPromise) return _scriptPromise;
  _scriptPromise = new Promise<void>((resolve, reject) => {
    if (document.querySelector(`script[src="${PASSAGE_V2_SRC}"]`)) {
      resolve();
      return;
    }
    const s = document.createElement("script");
    s.src = PASSAGE_V2_SRC;
    s.async = true;
    s.onload = () => resolve();
    s.onerror = () => {
      _scriptPromise = null;
      reject(new Error("Failed to load Valor Passage v2 SDK"));
    };
    document.head.appendChild(s);
  });
  return _scriptPromise;
}

export async function initializePassageV2Form(
  opts: PassageFormOptions,
): Promise<{ instance: PassageInstance }> {
  await _loadScript();

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const Passage = (window as any).PassageJS;
  if (!Passage) {
    throw new Error("Valor Passage SDK loaded but window.PassageJS is undefined");
  }

  const config: Record<string, unknown> = {
    clientToken: opts.clientToken,
    epi: opts.epi,
    ...(opts.formAction ? { formAction: opts.formAction } : {}),
    variant: "lightbox",
    enableACH: false,
    formFieldId: opts.hostElementId,
    ...(opts.isDemo ? { isDemo: true } : {}),
    ...(opts.defaults?.cardholderName
      ? { defaultCardholderName: opts.defaults.cardholderName }
      : {}),
    ...(opts.defaults?.phone ? { defaultPhone: opts.defaults.phone } : {}),
    ...(opts.defaults?.zip ? { defaultZip: opts.defaults.zip } : {}),
  };

  if (opts.onTokenReceived) {
    config.onTokenReceived = opts.onTokenReceived;
  }
  if (opts.onSuccess) {
    config.onSuccess = opts.onSuccess;
  }
  if (opts.onError) {
    config.onError = opts.onError;
  }
  if (opts.onSessionExpired) {
    config.onSessionExpired = opts.onSessionExpired;
  }

  const instance: PassageInstance = new Passage(config);
  return { instance };
}
